import { useEffect, useRef, useCallback } from 'react';
import { Square } from 'lucide-react';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client';
import { useMissionsStore } from '@/stores/missions-store';

/** LiveKit audio player — connects to an Echo channel for live listen-only audio */
export function EchoAudioPlayer(): React.JSX.Element | null {
  const livekit = useMissionsStore((s) => s.livekit);
  const listening = useMissionsStore((s) => s.listening);
  const audioError = useMissionsStore((s) => s.audioError);
  const setListening = useMissionsStore((s) => s.setListening);
  const setAudioError = useMissionsStore((s) => s.setAudioError);
  const clearLivekit = useMissionsStore((s) => s.clearLivekit);
  const roomRef = useRef<Room | null>(null);

  const handleStop = useCallback(() => {
    roomRef.current?.disconnect();
    roomRef.current = null;
    clearLivekit();
  }, [clearLivekit]);

  useEffect(() => {
    if (!livekit) return;

    const room = new Room({ adaptiveStream: true, dynacast: false });
    roomRef.current = room;

    const onTrackSubscribed = (
      track: Track,
      _pub: RemoteTrackPublication,
      _participant: RemoteParticipant,
    ): void => {
      if (track.kind === Track.Kind.Audio) {
        const el = track.attach();
        el.id = `echo-audio-${track.sid}`;
        document.body.appendChild(el);
      }
    };

    const onTrackUnsubscribed = (track: Track): void => {
      track.detach().forEach((el) => el.remove());
    };

    const onDisconnected = (): void => {
      setListening(false);
    };

    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    room.on(RoomEvent.Disconnected, onDisconnected);

    room
      .connect(livekit.wsUrl, livekit.token, { autoSubscribe: true })
      .then(() => {
        setListening(true);
        setAudioError(null);
      })
      .catch((err) => {
        setAudioError(err instanceof Error ? err.message : 'Connection failed');
        setListening(false);
      });

    return () => {
      room.removeAllListeners();
      room.disconnect();
      document.querySelectorAll('[id^=echo-audio-]').forEach((el) => el.remove());
    };
  }, [livekit, setListening, setAudioError]);

  if (!livekit) return null;

  return (
    <div className="flex items-center gap-2 rounded-md border border-green-500/20 bg-green-500/5 px-3 py-1.5">
      {listening ? (
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-red-400">
            Live Audio
          </span>
        </span>
      ) : audioError ? (
        <span className="flex-1 truncate text-[10px] text-red-400">{audioError}</span>
      ) : (
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-zinc-600 border-t-green-400" />
          <span className="text-[10px] text-muted-foreground">Connecting...</span>
        </span>
      )}
      <button
        onClick={handleStop}
        className="ml-auto flex items-center gap-1 rounded border border-zinc-700/30 bg-zinc-800/50 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground transition-colors hover:border-red-500/20 hover:bg-red-500/10 hover:text-red-400"
      >
        <Square className="h-2 w-2" />
        Stop
      </button>
    </div>
  );
}
